import { pickedFile } from '../../utils/pickedFile.js'
import { COMPANY_LOGO_HINT, companyLogoError } from '../../utils/companyLogoFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import companyService from '../../services/CompanyService.js';
import { Building2 } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import StateSelectField from '../common/StateSelectField.js';
import Button from '../common/Button.js';
import FeatureAccessFields, { getAgentNetworkCanAccessReset } from './FeatureAccessFields.js';
import CompanyIntegrationPanels from './CompanyIntegrationPanels.js';
import CompanyPersonalUpiFields from './CompanyPersonalUpiFields.js';
import { DEFAULT_CAN_ACCESS, getDefaultUserLimitForNetworkMode, mergeCanAccess, applyAttendanceFeatureState, getAttendanceCanAccessReset } from '../../utils/companyFeatures.js';
import { WHATSAPP_ACCOUNT_KEY, WHATSAPP_OPTIONS_KEY, isWhatsappAccountReady } from '../../utils/whatsappNotificationSettings.js';
import { useTranslation } from 'react-i18next';

const AddCompany = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    email: '',
    contact: '',
    support_contact: '',
    company_code: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    gst_number: '',
    ewaybill_username: '',
    ewaybill_password: '',
    pan_number: '',
    cst_number: '',
    cin_llpin: '',
    udyam_msme_number: '',
    tan_number: '',
    business_type: '',
    bank_name: '',
    branch_name: '',
    account_type: '',
    account_name: '',
    account_number: '',
    ifsc_code: '',
    status: 'active',
    from_date: '',
    to_date: '',
    user_limit: getDefaultUserLimitForNetworkMode('INTERNAL'),
    network_mode: 'INTERNAL',
    can_access: mergeCanAccess(DEFAULT_CAN_ACCESS),
    payment_gateway: {
      provider: 'razorpay',
      key_id: '',
      key_secret: '',
      webhook_secret: '',
    },
    company_upi_id: '',
    personal_upi_qr_enabled: false,
    branch_upi_qr_enabled: false,
    email_notification_settings: {
      mail_server: 'smtp.gmail.com',
      mail_port: 587,
      mail_use_tls: true,
      mail_use_ssl: false,
      mail_username: '',
      mail_password: '',
      mail_default_sender: '',
    },
  });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [sign, setSign] = useState(null);
  const [signPreview, setSignPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    company_name:   (v) => validators.required(v, 'Company name'),
    email:          (v) => validators.required(v, 'Email') || validators.email(v),
    contact:        (v) => validators.required(v, 'Contact') || validators.contactVarchar(v),
    support_contact:(v) => v ? validators.contactVarchar(v) : '',
    company_code:   (v) => validators.required(v, 'Company code') || validators.alphanumeric(v, 'Company code'),
    address:        (v) => validators.required(v, 'Address'),
    city:           (v) => validators.required(v, 'City') || validators.textOnly(v, 'City'),
    state:          (v) => validators.required(v, 'State') || validators.textOnly(v, 'State'),
    country:        (v) => validators.required(v, 'Country') || validators.textOnly(v, 'Country'),
    pincode:        (v) => validators.required(v, 'Pincode') || validators.pincode(v),
    gst_number:     (v) => v ? validators.gst(v) : '',
    pan_number:     (v) => v ? validators.pan(v) : '',
    ifsc_code:      (v) => v ? validators.ifsc(v) : '',
    account_number: (v) => v ? validators.alphanumeric(v, 'Account number') : '',
    from_date:      (v) => validators.required(v, 'From date'),
    to_date:        (v) => validators.required(v, 'To date'),
    user_limit:     (v) => {
      if (!v) return '';
      const n = Number(v);
      if (!Number.isInteger(n) || n < 1) return 'User limit must be a whole number of at least 1';
      return '';
    },
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'payment_gateway') {
      setFormData((prev) => ({ ...prev, payment_gateway: value }));
      return;
    }

    if (name === 'email_notification_settings') {
      setFormData((prev) => ({ ...prev, email_notification_settings: value }));
      return;
    }

    // Same pattern as Add User checkboxes (commission / pickup man).
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: Boolean(checked) }));
      return;
    }

    if (name === 'contact') {
      const phoneValue = inputFilters.phoneVarchar(value);
      setFormData((prev) => ({ ...prev, contact: phoneValue, support_contact: phoneValue }));
      validateField('contact', phoneValue, validationRules.contact);
      validateField('support_contact', phoneValue, validationRules.support_contact);
      return;
    }

    let filtered = value;
    if (name === 'support_contact') filtered = inputFilters.phoneVarchar(value);
    else if (name === 'pincode') filtered = inputFilters.pincode(value);
    else if (name === 'city' || name === 'state' || name === 'country') filtered = inputFilters.lettersOnly(value);
    else if (name === 'gst_number' || name === 'pan_number' || name === 'ifsc_code' || name === 'tan_number' || name === 'cin_llpin') filtered = value.toUpperCase();
    else if (name === 'company_code') filtered = value.toUpperCase();
    else if (name === 'account_number') filtered = inputFilters.digitsOnly(value);
    else if (name === 'user_limit') filtered = inputFilters.digitsOnly(value);

    if (name === 'network_mode' && filtered === 'EXTERNAL') {
      setFormData((prev) => ({
        ...prev,
        network_mode: filtered,
        user_limit: getDefaultUserLimitForNetworkMode('EXTERNAL'),
        can_access: {
          ...prev.can_access,
          ...getAttendanceCanAccessReset(),
        },
      }));
      return;
    }

    if (name === 'network_mode' && filtered === 'INTERNAL') {
      setFormData((prev) => ({
        ...prev,
        network_mode: filtered,
        user_limit: getDefaultUserLimitForNetworkMode('INTERNAL'),
        can_access: {
          ...prev.can_access,
          ...getAgentNetworkCanAccessReset(),
        },
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleFeatureToggle = (feature) => {
    setFormData((prev) => {
      if (
        ['attendance', 'attendance_manual', 'attendance_qr'].includes(feature)
        && prev.network_mode === 'EXTERNAL'
      ) {
        return prev;
      }

      const nextValue = !prev.can_access[feature];
      let nextCanAccess = {
        ...prev.can_access,
        [feature]: nextValue,
      };

      if (feature === 'attendance') {
        nextCanAccess = applyAttendanceFeatureState(prev.can_access, nextValue);
      }

      if (feature === 'agent_network' && !nextValue) {
        Object.assign(nextCanAccess, getAgentNetworkCanAccessReset());
      }

      return {
        ...prev,
        can_access: nextCanAccess,
      };
    });
  };

  const handleWhatsappSettingToggle = (eventKey, optionKey) => {
    setFormData((prev) => {
      const options = prev.can_access?.[WHATSAPP_OPTIONS_KEY] || {};
      return {
        ...prev,
        can_access: {
          ...prev.can_access,
          [WHATSAPP_OPTIONS_KEY]: {
            ...options,
            [eventKey]: {
              ...(options[eventKey] || {}),
              [optionKey]: !options?.[eventKey]?.[optionKey],
            },
          },
        },
      };
    });
  };

  const handleWhatsappAccountChange = (account) => {
    setFormData((prev) => ({
      ...prev,
      can_access: {
        ...prev.can_access,
        [WHATSAPP_ACCOUNT_KEY]: account,
      },
    }));
  };

  const handleWhatsappFieldChange = (eventKey, optionKey, value) => {
    setFormData((prev) => {
      const options = prev.can_access?.[WHATSAPP_OPTIONS_KEY] || {};
      return {
        ...prev,
        can_access: {
          ...prev.can_access,
          [WHATSAPP_OPTIONS_KEY]: {
            ...options,
            [eventKey]: {
              ...(options[eventKey] || {}),
              [optionKey]: value,
            },
          },
        },
      };
    });
  };

  const handleLogoChange = async (e) => {
    const file = pickedFile(e);
    if (!file) return;
    const error = await companyLogoError(file);
    if (error) {
      Alert.error(error);
      return;
    }
    setLogo(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoPreview(null);
    const fileInput = document.getElementById('logo-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSignChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        Alert.error('Signature must be less than 2MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        Alert.error('Please upload an image file');
        return;
      }
      setSign(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeSign = () => {
    setSign(null);
    setSignPreview(null);
    const fileInput = document.getElementById('sign-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    if (formData.from_date && formData.to_date && new Date(formData.to_date) <= new Date(formData.from_date)) {
      Alert.error('To Date must be after From Date');
      return;
    }

    if (formData.can_access?.whatsapp_notification
        && !isWhatsappAccountReady(formData.can_access?.[WHATSAPP_ACCOUNT_KEY])) {
      Alert.error('Enter WhatsApp Phone Number ID and Access Token for this company');
      return;
    }

    if (formData.personal_upi_qr_enabled && !(formData.company_upi_id || '').trim()) {
      Alert.error('Enter Company UPI ID (e.g. company@okaxis) or turn off personal company UPI QR');
      return;
    }

    if (
      formData.personal_upi_qr_enabled
      && (formData.company_upi_id || '').trim()
      && !(formData.company_upi_id || '').includes('@')
    ) {
      Alert.error('Company UPI ID must be a valid UPI ID (e.g. company@okaxis)');
      return;
    }

    setLoading(true);

    try {
      await companyService.create(formData, logo, sign);
      Alert.success('Company created successfully!');
      setTimeout(() => navigate('/companies'), 1500); // Correct route matches App.js definition
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 md:p-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-gray-200">
                  <div className="p-2 bg-orange-100 rounded-xl">
                    <Building2 className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-700">Add New Company</h2>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Create a new company in the system</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Basic Information</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div className="col-span-2 sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Company Name <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                          placeholder="Enter company name"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.company_name ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.company_name} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Email <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="company@example.com"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.email} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Contact <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="tel"
                          name="contact"
                          value={formData.contact}
                          onChange={handleChange}
                          placeholder="Enter 10-digit number"
                          maxLength={10}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.contact ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.contact} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Support Contact
                        </label>
                        <input
                          type="tel"
                          name="support_contact"
                          value={formData.support_contact}
                          onChange={handleChange}
                          placeholder="Enter 10-digit number"
                          maxLength={10}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.support_contact ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.support_contact} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Company Code <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="company_code"
                          value={formData.company_code}
                          onChange={handleChange}
                          placeholder="e.g. ABC01"
                          maxLength="5"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.company_code ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.company_code} />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Address Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Address Information</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div className="col-span-2 sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Address <span className="text-danger font-bold">*</span>
                        </label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="Enter full address"
                          rows="3"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.address ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.address} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          City <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="Enter city"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.city ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.city} />
                      </div>

                      <StateSelectField
                        label="State"
                        required
                        value={formData.state}
                        error={errors.state}
                        onChange={(state) => {
                          setFormData((prev) => ({ ...prev, state }));
                          validateField('state', state, validationRules.state);
                        }}
                      />

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Country <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          placeholder="Enter country"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.country ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.country} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Pincode <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          placeholder="Enter 6-digit pincode"
                          maxLength={6}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.pincode ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.pincode} />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Business Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Business Information</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          GST Number
                        </label>
                        <input
                          type="text"
                          name="gst_number"
                          value={formData.gst_number}
                          onChange={handleChange}
                          placeholder="Enter GST number"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.gst_number ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.gst_number} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          PAN Number
                        </label>
                        <input
                          type="text"
                          name="pan_number"
                          value={formData.pan_number}
                          onChange={handleChange}
                          placeholder="Enter PAN number"
                          maxLength={10}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.pan_number ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.pan_number} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          CST Number
                        </label>
                        <input
                          type="text"
                          name="cst_number"
                          value={formData.cst_number}
                          onChange={handleChange}
                          placeholder="Enter CST number"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          CIN/LLPIN
                        </label>
                        <input
                          type="text"
                          name="cin_llpin"
                          value={formData.cin_llpin}
                          onChange={handleChange}
                          placeholder="Enter CIN/LLPIN"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Udyam/MSME Number
                        </label>
                        <input
                          type="text"
                          name="udyam_msme_number"
                          value={formData.udyam_msme_number}
                          onChange={handleChange}
                          placeholder="Enter Udyam/MSME number"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          TAN Number
                        </label>
                        <input
                          type="text"
                          name="tan_number"
                          value={formData.tan_number}
                          onChange={handleChange}
                          placeholder="Enter TAN number"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Business Type
                        </label>
                        <input
                          type="text"
                          name="business_type"
                          value={formData.business_type}
                          onChange={handleChange}
                          placeholder="e.g. Private Limited, LLP"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Company Logo
                        </label>
                        <MediaCaptureField mode="photo" onPick={handleLogoChange} />
                        <p className="text-xs text-gray-600 mt-1">
                          {COMPANY_LOGO_HINT}
                        </p>
                        {logoPreview && (
                          <div className="mt-3">
                            <div className="relative inline-block">
                              <img 
                                src={logoPreview} 
                                alt="Logo Preview" 
                                className="h-16 sm:h-20 w-auto max-w-[280px] object-contain border border-gray-300 rounded-md bg-gray-50 px-3 py-2"
                              />
                              <button
                                type="button"
                                onClick={removeLogo}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove logo"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Authorised Signature
                        </label>
                        <MediaCaptureField mode="photo" onPick={handleSignChange} />
                        <p className="text-xs text-gray-600 mt-1">
                          Used on shipment invoices. PNG/JPG, max 2MB.
                        </p>
                        {signPreview && (
                          <div className="mt-3">
                            <div className="relative inline-block">
                              <img
                                src={signPreview}
                                alt="Signature Preview"
                                className="h-16 sm:h-20 w-auto max-w-[280px] object-contain border border-gray-300 rounded-md bg-gray-50 px-3 py-2"
                              />
                              <button
                                type="button"
                                onClick={removeSign}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove signature"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Banking Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Banking Details</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Bank Name
                        </label>
                        <input
                          type="text"
                          name="bank_name"
                          value={formData.bank_name}
                          onChange={handleChange}
                          placeholder="Enter bank name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Branch Name
                        </label>
                        <input
                          type="text"
                          name="branch_name"
                          value={formData.branch_name}
                          onChange={handleChange}
                          placeholder="Enter branch name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Account Type
                        </label>
                        <SelectField variant="form"
                          name="account_type"
                          value={formData.account_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          <option value="">Select account type</option>
                          <option value="Savings Account">Savings Account</option>
                          <option value="Current Account">Current Account</option>
                          <option value="Overdraft Account">Overdraft Account</option>
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Account Name
                        </label>
                        <input
                          type="text"
                          name="account_name"
                          value={formData.account_name}
                          onChange={handleChange}
                          placeholder="Enter account holder name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Account Number
                        </label>
                        <input
                          type="text"
                          name="account_number"
                          value={formData.account_number}
                          onChange={handleChange}
                          placeholder="Enter account number"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.account_number ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.account_number} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          IFSC Code
                        </label>
                        <input
                          type="text"
                          name="ifsc_code"
                          value={formData.ifsc_code}
                          onChange={handleChange}
                          placeholder="Enter IFSC code"
                          maxLength={11}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.ifsc_code ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.ifsc_code} />
                      </div>
                    </div>
                  </div>

                  {/* Section 5: Validity & Status */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Validity & Status</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          From Date <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="date"
                          name="from_date"
                          value={formData.from_date}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.from_date ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.from_date} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          To Date <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="date"
                          name="to_date"
                          value={formData.to_date}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.to_date ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.to_date} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Status <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          User Limit
                        </label>
                        <input
                          type="text"
                          name="user_limit"
                          value={formData.user_limit}
                          onChange={handleChange}
                          placeholder="Unlimited if empty"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.user_limit ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.user_limit} />
                        <p className="text-xs text-gray-500 mt-1">
                          Default: 5 for Company Owner, 1 for Agent companies
                        </p>
                      </div>
                    </div>
                  </div>

                  <FeatureAccessFields
                    formData={formData}
                    onNetworkModeChange={handleChange}
                    onFeatureToggle={handleFeatureToggle}
                  />

                  <CompanyIntegrationPanels
                    formData={formData}
                    onFeatureToggle={handleFeatureToggle}
                    onChange={handleChange}
                    onWhatsappToggle={handleWhatsappSettingToggle}
                    onWhatsappFieldChange={handleWhatsappFieldChange}
                    onWhatsappAccountChange={handleWhatsappAccountChange}
                  />

                  <CompanyPersonalUpiFields
                    formData={formData}
                    onChange={handleChange}
                    errors={errors}
                  />

                  {/* Form Actions */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 border-t">
                    <Button variant="primary" type="submit" loading={loading}>
                      {loading ? 'Creating...' : 'Create Company'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/companies')}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default AddCompany;